import { z } from 'zod';
import { ToolNameSchema } from './access.js';
import { CatalogSnapshotSchema, PublicationSchema } from './catalog.js';
import { IdSchema, PublicationIdSchema, ResultIdSchema, TenantIdSchema } from './common.js';
import { ChannelConfigSchema } from './config.js';
import { AuditRecordSchema, FeedbackRecordSchema } from './records.js';

/*
 * Contratos do painel administrativo (Self-hosted v1 e Cloud): o que fica no painel é negócio,
 * quem muda é o gestor, e vale na hora, sem reiniciar. Infraestrutura continua no lastrouter.yaml.
 */

const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'cor no formato #RRGGBB');
/** Imagem pequena embutida (logo, ícone). SVG fica de fora: pode carregar script. */
const ImageDataUrl = z
  .string()
  .max(300_000, 'imagem até 200 KB')
  .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/, 'use PNG, JPEG ou WebP');

/** Tema por cliente: só a camada de marca. Estrutura, espaçamento e tipografia são da Cluster. */
export const AppearanceSchema = z.object({
  assistant_name: z.string().min(1).max(60).default('Ask'),
  accent_color: HexColor.default('#1F5EFF'),
  logo: ImageDataUrl.nullable().default(null),
  icon: ImageDataUrl.nullable().default(null),
  /** Tom de voz geral do assistente, em todos os canais. Um app com a seção tom usa o dele. */
  voice: z.string().trim().max(1500).default(''),
});
export type Appearance = z.infer<typeof AppearanceSchema>;

export const LimitsSettingsSchema = z.object({
  /** Teto por usuário por minuto; sem valor, vale o do lastrouter.yaml. */
  calls_per_minute: z.number().int().min(1).max(10_000).nullable().default(null),
  per_channel: z.record(IdSchema, z.number().int().min(1).max(10_000)).default({}),
  /** Exceções por usuário (UPN em minúsculas). */
  per_user: z.record(z.string().min(3).max(256), z.number().int().min(1).max(10_000)).default({}),
});
export type LimitsSettings = z.infer<typeof LimitsSettingsSchema>;

export const TenantSettingsSchema = z.object({
  appearance: AppearanceSchema.default({ assistant_name: 'Ask', accent_color: '#1F5EFF', logo: null, icon: null, voice: '' }),
  limits: LimitsSettingsSchema.default({ calls_per_minute: null, per_channel: {}, per_user: {} }),
  /** Canais do painel. Nulo: valem os do lastrouter.yaml. */
  channels: z.array(ChannelConfigSchema).nullable().default(null),
  version: z.number().int().min(0).default(0),
  updated_at: z.string().nullable().default(null),
  updated_by: z.string().nullable().default(null),
});
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;
export type TenantSettingsInput = z.input<typeof TenantSettingsSchema>;

// ---------- Rascunhos do catálogo, aprovação dos dois donos e publicação ----------

export const DraftIdSchema = z.string().regex(/^d_[0-9A-HJKMNP-TV-Z]{26}$/, 'id de rascunho inválido');

export const ApprovalSchema = z.object({
  subject_id: IdSchema,
  papel: z.enum(['negocio', 'tecnico']),
  by: z.string().min(1),
  at: z.string(),
  decision: z.enum(['aprovado', 'recusado']),
  comment: z.string().max(2000).nullable().default(null),
  /** Dono ausente em nome de quem o admin decidiu (by é o admin). Só o servidor preenche, com justificativa. */
  no_lugar_de: z.string().min(1).optional(),
});
export type Approval = z.infer<typeof ApprovalSchema>;

export const DraftStatusSchema = z.enum(['aberto', 'aprovado', 'publicado', 'descartado']);

export const CatalogDraftSchema = z.object({
  id: DraftIdSchema,
  tenant_id: TenantIdSchema,
  base_publication_id: PublicationIdSchema.nullable(),
  snapshot: CatalogSnapshotSchema,
  status: DraftStatusSchema,
  reason: z.string().min(1).max(500),
  created_by: z.string().min(1),
  created_at: z.string(),
  updated_at: z.string(),
  /** Assuntos que o rascunho muda: cada um precisa da aprovação dos dois donos. */
  changed_subjects: z.array(IdSchema),
  changes: z.array(z.string()),
  approvals: z.array(ApprovalSchema).default([]),
  published_as: PublicationIdSchema.nullable().default(null),
});
export type CatalogDraft = z.infer<typeof CatalogDraftSchema>;

// ---------- Auditoria, feedback e qualidade ----------

export const AuditQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  user: z.string().max(256).optional(),
  tool: ToolNameSchema.optional(),
  status: z.enum(['ok', 'clarification', 'no_answer', 'error']).optional(),
  subject_id: IdSchema.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type AuditQuery = z.infer<typeof AuditQuerySchema>;

export const AuditPageSchema = z.object({ items: z.array(AuditRecordSchema), next_cursor: z.string().nullable() });
export type AuditPage = z.infer<typeof AuditPageSchema>;

export const AuditChainReportSchema = z.object({
  files: z.number().int(),
  records: z.number().int(),
  ok: z.boolean(),
  /** Primeira linha que não fecha a corrente, quando há. */
  broken_at: z.object({ file: z.string(), line: z.number().int(), reason: z.string() }).nullable(),
});
export type AuditChainReport = z.infer<typeof AuditChainReportSchema>;

export const FeedbackStatusSchema = z.enum(['aberto', 'em_revisao', 'resolvido', 'descartado']);
export const FeedbackItemSchema = FeedbackRecordSchema.extend({
  status: FeedbackStatusSchema.default('aberto'),
  resolution: z.string().max(2000).nullable().default(null),
  updated_by: z.string().nullable().default(null),
  updated_at: z.string().nullable().default(null),
});
export type FeedbackItem = z.infer<typeof FeedbackItemSchema>;

/**
 * Pergunta como chegou, para a avaliação online e para virar caso da suíte offline.
 * Guarda o texto (não o resultado) com retenção própria; o usuário vai como hash.
 */
export const QuestionLogRecordSchema = z.object({
  id: z.string(),
  ts: z.string(),
  tenant_id: TenantIdSchema,
  correlation_id: z.string(),
  user_hash: z.string(),
  channel: z.string(),
  question: z.string().max(1000),
  clarification: z.string().max(200).nullable(),
  status: z.enum(['answered', 'needs_clarification', 'no_answer', 'error']),
  subject_id: IdSchema.nullable(),
  query_id: IdSchema.nullable(),
  result_id: ResultIdSchema.nullable(),
});
export type QuestionLogRecord = z.infer<typeof QuestionLogRecordSchema>;

/** Avaliação online por assunto e semana ISO (AAAA-Www). */
export const QualityWeekSchema = z.object({
  subject_id: IdSchema.nullable(),
  week: z.string().regex(/^\d{4}-W\d{2}$/),
  questions: z.number().int(),
  answered: z.number().int(),
  clarifications: z.number().int(),
  no_answer: z.number().int(),
  errors: z.number().int(),
  negative_feedback: z.number().int(),
  answer_rate: z.number().nullable(),
  /** Queda de mais de 10 pontos contra a semana anterior: abre revisão com o dono. */
  needs_review: z.boolean(),
});
export type QualityWeek = z.infer<typeof QualityWeekSchema>;

/** Correção de contexto proposta a partir de conversas com problema. Uma pessoa aprova. */
export const ContextProposalSchema = z.object({
  id: z.string(),
  tenant_id: TenantIdSchema,
  subject_id: IdSchema,
  kind: z.enum(['sinonimo', 'exemplo', 'nova_query', 'revisao']),
  target: z.object({ type: z.enum(['metrica', 'dimensao', 'query', 'assunto']), id: z.string() }),
  suggestion: z.string().max(2000),
  evidence: z.object({ questions: z.array(z.string()).max(20), feedback_ids: z.array(z.string()).max(20) }),
  status: z.enum(['proposta', 'aceita', 'recusada']),
  created_at: z.string(),
  decided_by: z.string().nullable().default(null),
  decided_at: z.string().nullable().default(null),
});
export type ContextProposal = z.infer<typeof ContextProposalSchema>;

// ---------- Visão geral, conexões, sessão ----------

export const ConnectionStatusSchema = z.object({
  id: IdSchema,
  type: z.string(),
  /** Endereço sem segredo (host ou URL do tenant). */
  target: z.string(),
  ok: z.boolean(),
  detail: z.string(),
  checked_at: z.string(),
  managed_by: z.enum(['arquivo', 'painel']),
});
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

export const ConnectionTestInputSchema = z.object({ user: z.string().min(1).max(256), subject_id: IdSchema.optional() });

export const ConnectionTestResultSchema = z.object({
  ok: z.boolean(),
  user: z.string(),
  apps: z.array(z.object({ subject_id: IdSchema, app: z.string(), ok: z.boolean(), title: z.string().nullable(), last_reload_at: z.string().nullable(), detail: z.string() })),
});

export const OverviewSchema = z.object({
  version: z.string(),
  edition: z.enum(['selfhosted', 'cloud']),
  tenant_id: TenantIdSchema,
  publication: PublicationSchema.nullable(),
  license: z.object({ status: z.enum(['valida', 'tolerancia', 'vencida', 'ausente', 'invalida']), cliente: z.string().nullable(), validade: z.string().nullable(), detail: z.string() }),
  connections: z.array(ConnectionStatusSchema),
  last_7_days: z.object({
    calls: z.number().int(),
    users: z.number().int(),
    answered: z.number().int(),
    clarifications: z.number().int(),
    errors: z.number().int(),
    negative_feedback: z.number().int(),
  }),
  open_feedback: z.number().int(),
  open_drafts: z.number().int(),
});
export type Overview = z.infer<typeof OverviewSchema>;

export const SessionSchema = z.object({
  user: z.string(),
  name: z.string().nullable(),
  roles: z.array(z.string()),
  is_admin: z.boolean(),
  /** Assuntos de que a pessoa é dona (negócio ou técnico): pode aprovar rascunhos deles. */
  owner_of: z.array(z.object({ subject_id: IdSchema, papel: z.enum(['negocio', 'tecnico']) })),
  csrf_token: z.string(),
  expires_at: z.string(),
});
export type Session = z.infer<typeof SessionSchema>;

export const AccessSimulationInputSchema = z.object({
  upn: z.string().min(3).max(256),
  roles: z.array(z.string().max(120)).max(100),
  client_app: z.string().max(200).nullable().default(null),
});

export const AccessSimulationSchema = z.object({
  channel: z.string().nullable(),
  refused: z.string().nullable(),
  tools: z.array(ToolNameSchema),
  /** Apps que a pessoa enxerga na fonte, como ela (ADR 0036). */
  subjects: z.array(z.object({ id: IdSchema, nome: z.string() })),
  source_users: z.array(z.object({ source_id: IdSchema, user: z.string().nullable(), detail: z.string() })),
});
export type AccessSimulation = z.infer<typeof AccessSimulationSchema>;

export const EvalReportRecordSchema = z.object({
  id: z.string(),
  tenant_id: TenantIdSchema,
  created_at: z.string(),
  created_by: z.string(),
  publication_id: PublicationIdSchema.nullable(),
  label: z.string(),
  answer_rate: z.number().nullable(),
  accuracy: z.number().nullable(),
  certified_wrong: z.number().int(),
  deterministic: z.object({ passed: z.number().int(), total: z.number().int() }).nullable(),
  markdown: z.string().max(2_000_000),
});
export type EvalReportRecord = z.infer<typeof EvalReportRecordSchema>;
